import React, { Suspense, lazy, useEffect, useState } from 'react'
import { notifyError } from '../../../utils/notifyError'
import { type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import PAGE_TITLES from '../../../consts/titles'
import LoadingPageV2 from '../../../pages/LoadingPageV2'
import { Box } from '@mui/material'
import api from '../../../utils/api'
import { useQuery } from '@tanstack/react-query'
import { type IAttributeList } from '../../../models/Attributes'
import { ATTRIBUTE_TYPES } from '../../../consts'
import { CreateAndEditFacultyAttributes } from './CreateAndEditFacultyAttributes'
import DeleteAttributes from '../CourseAttributes/DeleteAttributes'
import { reportingColumnOverrides } from '../reportingColumnOverride'

const AppTable = lazy(async () => await import('../../../components/CustomTable'))

interface ParamsTypes extends ApplyFiltersAndDispatchParams {
  attributeType?: string
}

const FacultyAttributes: React.FC = () => {
  const [params, setParms] = useState<ParamsTypes>({ pageSize: 10, attributeType: ATTRIBUTE_TYPES.FACULTY_COURSE_ATTRIBUTES })

  const { data: attributesList, isLoading, isFetching, refetch, isError: isAttributesError, error: attributesError } = useQuery({
    queryKey: [ATTRIBUTE_TYPES.FACULTY_COURSE_ATTRIBUTES, params],
    queryFn: async (): Promise<IAttributeList> => {
      const result = await api.get('/api/attributes/', {
        params,
        headers: {
          Accept: 'application/cdg'
        }
      })
      return result.data
    }
  })

  useEffect(() => {
    if (isAttributesError) notifyError(attributesError, { fallbackMessage: 'Failed to load faculty attributes. Please try again.' })
  }, [isAttributesError])
  const handleRefetch = (): void => {
    void refetch()
  }

  return (
    <Box mt={5}>
      <Suspense fallback={<LoadingPageV2 title={PAGE_TITLES.ATTRIBUTES} />}>
        <AppTable
          list={attributesList}
          loading={isLoading || isFetching}
          featureName="Faculty Attribute"
          params={params}
          setParams={setParms}
          createActionTitle='Create Attribute'
          createAction={<CreateAndEditFacultyAttributes refetch={handleRefetch} />}
          editAction={<CreateAndEditFacultyAttributes refetch={handleRefetch} />}
          deleteAction={<DeleteAttributes refetch={handleRefetch} />}
          overrides={reportingColumnOverrides}
          actionModalEntityDisplayField='attributeKey'
          noDataComponentProps={{
            title: 'Start by creating an attribute for your faculty',
            subTitle: 'Use attributes to target your surveys to a more precise audience.'
          }}
        />
      </Suspense>
    </Box>
  )
}

export default FacultyAttributes
